import useCustomerQueryCaa from "@repo/shopify/queries/useCustomerQueryCaa";
import type { ICaaCustomerAddress } from "@repo/shopify/queries/customer-account-gql";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import Button from "@atoms/Button";

import AddressForm from "@organisms/Address/AddressForm";
import AddressFormIntl from "@organisms/Address/AddressFormIntl";
import AddressFormJp from "@organisms/Address/AddressFormJp";
import AddressItem from "@organisms/Address/AddressItem";

import useAddresses from "@hooks/useAddresses";
import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import apolloCaaClient from "@lib/apolloCaa";

import { useLayoutStore } from "@store/layout";

export default function Addresses() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { showFooter } = useLayoutStore();
  const isJp = lang === "jp";

  const {
    showForm,
    setShowForm,
    editableAddress,
    setEditableAddress,
    onCompletedMutation,
    openAddressForm,
    defaultAddress,
    otherAddresses,
  } = useAddresses(isJp);

  const { deleteAddress, deleteAddressLoading } = useCustomerQuery();
  const { deleteCaaAddress, deleteCaaAddressLoading } = useCustomerQueryCaa({ client: apolloCaaClient });

  const isDeleteLoading = isJp ? deleteCaaAddressLoading : deleteAddressLoading;

  const handleClickEdit = (address: MailingAddress | ICaaCustomerAddress) => {
    window.scrollTo(0, 0);
    openAddressForm(address);
  };

  const handleDelete = async (address: MailingAddress | ICaaCustomerAddress) => {
    if (isJp) {
      await deleteCaaAddress({ variables: { addressId: address.id } });
    } else {
      await deleteAddress({ variables: { id: address.id } });
    }
    await onCompletedMutation();
  };

  useEffect(() => {
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const handleCancelForm = () => {
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const handleFormCompleted = async () => {
    await onCompletedMutation();
    setShowForm(false);
    setEditableAddress(undefined);
  };

  return (
    <section className="tnt-gap-x pc:col-span-12 pc:grid-cols-12 col-span-8 grid gap-y-10">
      {showForm ? (
        <div className="col-span-8 flex flex-col gap-5">
          <div className="flex flex-col gap-2.5">
            <Button as="text" className="col-span-12" onClick={handleCancelForm}>
              ← Addresses
            </Button>
            <h2 className="text-title col-span-12">{editableAddress ? "Edit a Address" : "Add a New Address"}</h2>
          </div>
          {lang === "ko" && (
            <AddressForm
              address={editableAddress as MailingAddress | undefined}
              className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
              isDefaultAddress={defaultAddress?.id === editableAddress?.id}
              onClickCancel={handleCancelForm}
              onCompletedMutation={handleFormCompleted}
            />
          )}
          {lang === "en" && (
            <AddressFormIntl
              address={editableAddress as MailingAddress | undefined}
              className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
              isDefaultAddress={defaultAddress?.id === editableAddress?.id}
              isEmptyAddress={!defaultAddress?.id}
              onClickCancel={handleCancelForm}
              onCompletedMutation={handleFormCompleted}
            />
          )}
          {lang === "jp" && (
            <AddressFormJp
              address={editableAddress as ICaaCustomerAddress | undefined}
              className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
              isDefaultAddress={defaultAddress?.id === editableAddress?.id}
              isEmptyAddress={!defaultAddress?.id}
              onClickCancel={handleCancelForm}
              onCompletedMutation={handleFormCompleted}
            />
          )}
        </div>
      ) : (
        <>
          <div className="pc:col-span-12 col-span-8 flex flex-col gap-5">
            <h2 className="text-title">Addresses</h2>
            <p>
              <Trans i18nKey="addresses.description" />
            </p>
            <Button
              as="square"
              onClick={() => {
                window.scrollTo(0, 0);
                setShowForm(true);
              }}
            >
              {t("addresses.create_new_address")}
            </Button>
          </div>

          <div className="pc:col-span-12 col-span-8">
            {Boolean(defaultAddress) || otherAddresses.length ? (
              <ul className="tnt-gap-x pc:grid-cols-12 grid gap-y-10">
                {defaultAddress ? (
                  <li className="pc:col-span-2 col-span-8">
                    <AddressItem
                      address={defaultAddress}
                      isDefaultAddress
                      isDeleteLoading={isDeleteLoading}
                      isJp={isJp}
                      onClickEdit={() => {
                        handleClickEdit(defaultAddress);
                      }}
                      onDelete={() => handleDelete(defaultAddress)}
                    />
                  </li>
                ) : null}

                {otherAddresses.map((node) => (
                  <li className="pc:col-span-2 col-span-8" key={node.id}>
                    <AddressItem
                      address={node}
                      isDefaultAddress={defaultAddress?.id === node.id}
                      isDeleteLoading={isDeleteLoading}
                      isJp={isJp}
                      onClickEdit={() => {
                        handleClickEdit(node);
                      }}
                      onDelete={() => handleDelete(node)}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
